import React, {useState, useEffect, Fragment, useMemo} from 'react';
import styled, {useTheme} from 'styled-components';
import {getThemedSelectMenuProps} from 'utils/muiMenuProps';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableHead from 'components/Tables/TableStructure/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from 'components/Tables/TableStructure/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TablePaginationActions from 'components/Tables/TableStructure/TablePaginationActions';
import TablePaginationActionsStore from 'components/Tables/TableStructure/TablePaginationActionsStore';
import SGBody from 'components/Typography/SGBody';
import LoadingNoDataTable from 'components/Tables/TableStructure/LoadingNoData';
import {useSelector} from 'react-redux';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {default as AlertComponent} from 'features/POSRegisters/components/Alert';
import {AlertType} from 'features/POSRegisters/interfaces/AlertInfo';

const StyledTableContainer = styled(TableContainer)`
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    background-color: ${props => props.theme.disabled}22;
  }

  ::-webkit-scrollbar-thumb {
    border: 1px solid ${props => props.theme.navText}22;
    background-color: ${props => props.theme.navText}22;

    &:hover {
      border: 1px solid ${props => props.theme.navText}99;
      background-color: ${props => props.theme.navText}99;
    }
  }

  ::-webkit-resizer {
    display: none;
  }

  display: flex;
  justify-content: space-between;
  flex-direction: column;

  &.scroll {
    max-height: 550px;
    overflow-y: auto;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;
  table-layout: fixed;
  &.scroll tbody {
    overflow-y: auto; /* Trigger vertical scroll    */
    overflow-x: hidden; /* Hide the horizontal scroll */
  }

  .MuiAccordion-root {
    margin-bottom: 8px;
  }

  .MuiAccordionSummary-content {
    max-height: 24px;
    margin: 0;
  }
`;

const StyledSpacer = styled.div`
  width: 100%;
  height: 2rem;
`;

const StyledTablePagination = styled(TablePagination)`
  color: ${props => props.theme.subtleText};
  .MuiIconButton-label {
    /* color: ${props => props.theme.subtleText}; */
  }
  .MuiSelect-icon {
    color: ${props => props.theme.subtleText};
  }
  .MuiIconButton-root.Mui-disabled {
    color: ${props => props.theme.disabled};
  }
  .MuiTablePagination-caption:nth-of-type(2) {
    display: none;
  }
`;

const StyledTableRow = styled(TableRow)`
  &.mouseHover {
    cursor: pointer;
  }

  .MuiTableCell-root.MuiTableCell-body {
    border-bottom: 0;
  }
`;

const StyledTableCell = styled(TableCell)`
  background-color: ${props => props.theme.cardBg};
  padding: 8px 6px;

  &.extra-padding {
    //visually center table contents with sort label
    padding-right: 26px;
  }
`;

const StyledCellText = styled(SGBody)`
  word-wrap: break-word;
`;

const StyledTableRowDivider = styled.tr`
  td {
    border-bottom: 1px solid ${props => props.theme.grey};
  }

  &:last-child td {
    border-color: transparent;
  }
`;

export function withAlerts(devices, scanSubType) {
  return devices.map(device => {
    const alerts = !!scanSubType ? getSubTypeAlerts(device.alerts, scanSubType) : device.alerts;

    return {
      ...device,
      extraContent: (
        <Fragment>
          {alerts?.map((alert, alertIdx) => {
            const rows = [{label: 'Issue', value: alert.description, fullWidth: true}];

            if (alert.alertType !== AlertType.SCAN_FAILURE_ALERT) {
              rows.push({label: 'Effect', value: alert.effect, fullWidth: true});
              rows.push({
                label: 'Solution',
                value: `${alert.solution ?? ''} ${alert.kb ?? ''}`,
                fullWidth: true,
              });
            }

            return (
              <AlertComponent
                key={`${device.scanResults.deviceName}-${alertIdx}`}
                device={device}
                alert={alert}
                rows={rows}
              />
            );
          })}
        </Fragment>
      ),
    };
  });
}

export default function TableStructure(props) {
  const {
    columns,
    data,
    loaded,
    noDataText,
    clickAction,
    clickActionType,
    disablePagination,
    paginationType,
    paginationText = 'Events',
    customRowsPerPage,
    customRowClass,
    showAll,
    className,
  } = props;

  const theme = useTheme();
  const selectProps = {
    MenuProps: getThemedSelectMenuProps(theme),
  };

  const tableScroll = useSelector(state => state.ui.tableScroll);
  const defaultRowsPerPage = customRowsPerPage?.defaultRowsPerPage
    ? customRowsPerPage.defaultRowsPerPage
    : 5;

  const rowsPerPageOptions = customRowsPerPage?.rowsPerPageOptions
    ? customRowsPerPage.rowsPerPageOptions
    : [5, 10, 25, {label: 'All', value: -1}];

  const [rows, setRows] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function descendingComparator(a, b, orderBy) {
    if (a.scanResults?.[orderBy] === undefined) return 0;

    const A = a.scanResults?.[orderBy].toString().toLowerCase();
    const B = b.scanResults?.[orderBy].toString().toLowerCase();

    if (isNaN(Number(A)) && isNaN(Number(B))) {
      // if both are strings
      if (A < B) {
        return -1;
      }
      if (A > B) {
        return 1;
      }
      return 0;
    } else if (isNaN(Number(A))) {
      // if A is a string
      return 1;
    } else if (isNaN(Number(B))) {
      // if B is a string
      return -1;
    } else {
      // if both are numbers
      return Number(A) - Number(B);
    }
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  }

  const rowsList = useMemo(
    () =>
      rowsPerPage > 0
        ? stableSort(rows, getComparator(order, orderBy)).slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
          )
        : stableSort(rows, getComparator(order, orderBy)),
    [rows, order, orderBy, page, rowsPerPage]
  );

  useEffect(() => {
    setRows(data);
    setPage(0);
  }, [data]);

  useEffect(() => {
    showAll || tableScroll ? setRowsPerPage('All') : setRowsPerPage(defaultRowsPerPage);
  }, [showAll, tableScroll, defaultRowsPerPage]);

  const customPagination = props => {
    return <TablePaginationActions {...props} paginationText={paginationText} />;
  };

  return (
    <>
      <StyledTableContainer className={(tableScroll ? 'scroll ' : ' ') + className}>
        <StyledTable
          stickyHeader
          aria-label="sticky table"
          className={tableScroll ? 'scroll ' : ' '}
        >
          <TableHead
            columns={columns}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {rowsList.map((row, index) => (
              <Fragment key={`${row.scanResults?.deviceName}-${index}`}>
                <StyledTableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  onClick={clickAction}
                  clickkey={row[clickActionType]}
                  className={
                    (clickAction ? 'mouseHover ' : ' ') +
                    (customRowClass ? customRowClass(row) : '')
                  }
                >
                  {columns.map(column => {
                    const value = row.scanResults?.[column.id];
                    return (
                      <StyledTableCell
                        body
                        key={column.id + row.id}
                        style={{width: column.width}}
                        align={column.align}
                        className={`body-cell ${
                          column.align === 'center' ? 'extra-padding ' : ' '
                        } ${column.customClass} `}
                      >
                        {column.format ? (
                          column.format(value, row)
                        ) : (
                          <StyledCellText variant={'b4'}>{value}</StyledCellText>
                        )}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
                {row.extraContent && (
                  <tr>
                    <td colSpan={12}>{row.extraContent}</td>
                  </tr>
                )}
                <StyledTableRowDivider>
                  <td colSpan={12} />
                </StyledTableRowDivider>
              </Fragment>
            ))}
          </TableBody>
        </StyledTable>
        <LoadingNoDataTable dataCount={rows.length} noDataText={noDataText} isLoaded={loaded} />
      </StyledTableContainer>
      {tableScroll || disablePagination ? (
        <StyledSpacer className={'end-table-spacer'} />
      ) : (
        <StyledTablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          ActionsComponent={paginationType ? TablePaginationActionsStore : customPagination}
          SelectProps={selectProps}
        />
      )}
    </>
  );
}
